import dayjs from 'dayjs';
import {
 ProjectRegistration, OrganizationSubmission, ProjectSubmission, ContactSubmission, ProjectRegistrationStorePayload,
} from '../models';
import { SubmissionSource, RequestStatus } from '../models/enums';
import { FilemakerCreateProject } from '../models/filemakerSubmissions/FilemakerCreateProject.model';
import filemakerService from './filemakerMapper.service';

const buildCreatePayload = (payload: ProjectRegistrationStorePayload): ProjectRegistration => {
  const projectSubmission: ProjectSubmission = {
    name: payload.projectName,
    city: payload.projectCity,
    country: payload.projectCountry,
    stateProvince: payload.projectStateProvince,
    standards: payload.projectStandards,
    statusAtRegistration: payload.projectStatus,
    status: 'Awarded to WW',
    estimatedShopDrawingStartDate: payload.projectEstimatedShopDrawingStart,
    estimatedCompletionDate: payload.projectCompletionDate,
    source: SubmissionSource.ProjectForm,
    projectBidDate: payload.projectBidDate,
  };

  const firm: OrganizationSubmission = {
    name: payload.firmName,
    addressLine1: payload.firmAddressLine1,
    addressLine2: payload.firmAddressLine2,
    city: payload.firmCity,
    country: payload.firmCountry,
    state: payload.firmStateProvince,
    zip: payload.firmZip,
    phone: payload.firmPhone,
    fax: payload.firmFax,
    email: payload.firmEmail,
    source: SubmissionSource.ProjectForm,
  };

  const firmContact: ContactSubmission = {
    firstName: payload.firmContactFirstName,
    lastName: payload.firmContactLastName,
    organizationName: payload.firmName,
    email: payload.firmEmail,
    source: SubmissionSource.ProjectForm,
    memberType: 'AD',
  };

  const contractor: OrganizationSubmission = {
    name: payload.contractorName,
    addressLine1: payload.contractorAddressLine1,
    addressLine2: payload.contractorAddressLine2,
    city: payload.contractorCity,
    state: payload.contractorStateProvince,
    zip: payload.contractorZip,
    country: payload.contractorCountry,
    phone: payload.contractorPhone,
    fax: payload.contractorFax,
    email: payload.contractorEmail,
    source: SubmissionSource.ProjectForm,
  };

  const contractorContact: ContactSubmission = {
    firstName: payload.contractorFirstName,
    lastName: payload.contractorLastName,
    organizationName: payload.contractorName,
    email: payload.contractorEmail,
    source: SubmissionSource.ProjectForm,
    memberType: 'GC',
  };

  const woodworker: OrganizationSubmission = {
    name: payload.woodworkerName,
    addressLine1: payload.woodworkerAddressLine1,
    addressLine2: payload.woodworkerAddressLine2,
    city: payload.woodworkerCity,
    country: payload.woodworkerCountry,
    state: payload.woodworkerStateProvince,
    zip: payload.woodworkerZip,
    phone: payload.woodworkerPhone,
    fax: payload.woodworkerFax,
    email: payload.woodworkerEmail,
    source: SubmissionSource.ProjectForm,
  };

  const woodworkerContact: ContactSubmission = {
    firstName: payload.woodworkerContactFirstName,
    lastName: payload.woodworkerContactLastName,
    email: payload.woodworkerEmail,
    phone: payload.woodworkerPhone,
    organizationName: payload.woodworkerName,
    source: SubmissionSource.ProjectForm,
    memberType: 'PROJ WW',
  };

  return {
    projectInfo: projectSubmission,
    revisedProjectInfo: projectSubmission,

    firm,
    revisedFirm: firm,

    firmContact,
    revisedFirmContact: firmContact,

    contractor,
    revisedContractor: contractor,

    contractorContact,
    revisedContractorContact: contractorContact,

    submitterName: payload.submitterName,
    revisedSubmitterName: payload.submitterName,

    submitterEmail: payload.submitterEmail,
    revisedSubmitterEmail: payload.submitterEmail,

    submitterProjectRole: payload.submitterProjectRole,
    revisedSubmitterProjectRole: payload.submitterProjectRole,

    woodworker,
    revisedWoodworker: woodworker,

    woodworkerContact,
    revisedWoodworkerContact: woodworkerContact,

    sendCredentialsToWoodworker: payload.sendCredentialsToWoodworker,
    revisedSendCredentialsToWoodworker: payload.sendCredentialsToWoodworker,

    sendCredentialsToFirm: payload.sendCredentialsToDesignFirm,
    revisedSendCredentialsToFirm: payload.sendCredentialsToDesignFirm,

    sendCredentialsToContractor: payload.sendCredentialsToContractor,
    revisedSendCredentialsToContractor: payload.sendCredentialsToContractor,
    status: RequestStatus.Pending,
  };
};

export const mapProjectSubmissionToFileMakerCreateProject = (submission: ProjectSubmission): FilemakerCreateProject => {
  const initialMappedProject = filemakerService.mapProjectSubmissionToFileMakerCreateProject(submission);
  initialMappedProject['PR Status'] = 'Awarded to WW';
  initialMappedProject['Date Next Contact'] = dayjs().add(2, 'month').date(1).format('MM/DD/YYYY');
  initialMappedProject.Source = 'WS';

    return initialMappedProject;
 };

export const mapFilemakerProjectToProjectSubmission = (filemakerProject, source: SubmissionSource): ProjectSubmission => ({
  name: filemakerProject['PR Name'],
  number: filemakerProject['QCP Project Number Calc'],
  city: filemakerProject['PR City'],
  stateProvince: filemakerProject['PR St'],
  source,
  country: '',
  estimatedCompletionDate: filemakerProject['Estimated Completion Date of Project'],
  standards: filemakerProject['Referenced Standard'],
  status: filemakerProject['PR Status'],
  estimatedShopDrawingStartDate: null,
  projectBidDate: filemakerProject['Bid Date'],
});

export const mapFilemakerFirmToFirmSubmission = (filemakerProject, source: SubmissionSource): OrganizationSubmission => ({
  name: filemakerProject['Organization Name'],
  addressLine1: filemakerProject['Address Line1'],
  addressLine2: filemakerProject['Address Line2'],
  city: filemakerProject['Address City'],
  state: filemakerProject['Address State'],
  zip: filemakerProject['Address Zip'],
  source,
  country: filemakerProject['Address Country'],
  status: filemakerProject.Status,
  membershipType: filemakerProject['Membership Type Calc'],
  phone: filemakerProject.Phone,
  fax: filemakerProject['Phone Fax'],
  email: filemakerProject['Organization Email Address'],
  filemakerOrganizationId: filemakerProject['Organization ID'],
});

export const mapFilemakerContactToContactSubmission = (filemakerProject, source: SubmissionSource): ContactSubmission => ({
  filemakerContactId: filemakerProject['Contact ID'],
  jobTitle: filemakerProject['Job Title'],
  memberType: filemakerProject['Member Type'],
  firstName: filemakerProject['Name First Name'],
  lastName: filemakerProject['Name Last Name'],
  email: filemakerProject.Email,
  phone: filemakerProject.Phone,
  fax: filemakerProject['Phone Fax'],
  filemakerOrganizationId: filemakerProject['Organization ID'],
  source,
  organizationName: filemakerProject['Organization Name Calc'],
});

export default {
  buildCreatePayload,
  mapProjectSubmissionToFileMakerCreateProject,
  mapFilemakerProjectToProjectSubmission,
  mapFilemakerFirmToFirmSubmission,
  mapFilemakerContactToContactSubmission,
};